import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAgreementAcceptance,
  getApplicableAgreementAcceptance,
  getCurrentLegalDocuments,
} from "@/lib/commercialAgreementAcceptancesApi";
import {
  buildAgreementAcceptancePayload,
  hasActiveCommercialLicense,
  LICENSE_REQUEST_MSG_LEGAL_LOAD_FAILED,
  LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED,
  resolveActiveModuleIds,
  resolveCommercialModuleCatalog,
  resolveRequestableModuleIds,
} from "@/lib/commercialLicenseRequests";

/**
 * Aceptación legal compartida — persiste evidencia al marcar y bloquea edición.
 * En modo ampliación (`expansionMode`), reutiliza aceptación vigente si aplica.
 * @param {boolean} [enabled]
 * @param {{ form?: object, user?: object, systemInfo?: object }} [ctx]
 * @param {{ expansionMode?: boolean, expansionModulesForLegal?: Record<string, boolean> }} [options]
 */
export function useCommercialLegalAcceptance(enabled = true, ctx = {}, options = {}) {
  const { form, user, systemInfo } = ctx;
  const { expansionMode = false, expansionModulesForLegal = {} } = options;
  const [legalCatalog, setLegalCatalog] = useState(null);
  const [legalLoading, setLegalLoading] = useState(Boolean(enabled));
  const [legalError, setLegalError] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalLocked, setLegalLocked] = useState(false);
  const [legalSaving, setLegalSaving] = useState(false);
  const [legalAcceptError, setLegalAcceptError] = useState("");
  const [agreementAcceptanceId, setAgreementAcceptanceId] = useState("");
  const [legalReused, setLegalReused] = useState(false);
  const [reusedAcceptance, setReusedAcceptance] = useState(null);
  const [applicableLoading, setApplicableLoading] = useState(false);

  const catalog = useMemo(
    () => (systemInfo ? resolveCommercialModuleCatalog(systemInfo) : null),
    [systemInfo],
  );

  const expansionModulesKey = useMemo(
    () => JSON.stringify(expansionModulesForLegal || {}),
    [expansionModulesForLegal],
  );

  useEffect(() => {
    if (!enabled) {
      setLegalLoading(false);
      setLegalCatalog(null);
      setLegalError("");
      setLegalAccepted(false);
      setLegalLocked(false);
      setAgreementAcceptanceId("");
      setLegalAcceptError("");
      setLegalReused(false);
      setReusedAcceptance(null);
      return undefined;
    }
    let cancelled = false;
    setLegalLoading(true);
    setLegalError("");
    getCurrentLegalDocuments("license_request")
      .then((data) => {
        if (!cancelled) {
          setLegalCatalog(data);
          if (!legalLocked && !expansionMode) {
            setLegalAccepted(false);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLegalCatalog(null);
          setLegalError(LICENSE_REQUEST_MSG_LEGAL_LOAD_FAILED);
        }
      })
      .finally(() => {
        if (!cancelled) setLegalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, legalLocked, expansionMode]);

  useEffect(() => {
    if (!enabled || !expansionMode || legalLoading) return undefined;
    let cancelled = false;
    setApplicableLoading(true);
    getApplicableAgreementAcceptance({ requested_modules: expansionModulesForLegal })
      .then((data) => {
        if (cancelled) return;
        if (data?.reusable && data?.agreement_acceptance_id) {
          setLegalReused(true);
          setReusedAcceptance(data.acceptance || data);
          setAgreementAcceptanceId(String(data.agreement_acceptance_id));
          setLegalAccepted(true);
          setLegalLocked(true);
          setLegalAcceptError("");
        } else {
          setLegalReused(false);
          setReusedAcceptance(null);
          if (!legalSaving) {
            setAgreementAcceptanceId("");
            setLegalAccepted(false);
            setLegalLocked(false);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLegalReused(false);
          setReusedAcceptance(null);
        }
      })
      .finally(() => {
        if (!cancelled) setApplicableLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, expansionMode, expansionModulesKey, legalLoading, legalSaving]);

  const handleLegalAcceptedChange = useCallback(
    async (checked) => {
      if (legalLocked || legalSaving || legalReused) return;
      if (!checked) {
        if (!legalLocked) setLegalAccepted(false);
        return;
      }
      if (!form || !user || !legalCatalog) {
        setLegalAcceptError(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
        return;
      }
      setLegalSaving(true);
      setLegalAcceptError("");
      try {
        const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
        const activeModuleIds = resolveActiveModuleIds(systemInfo);
        const requestableModuleIds = resolveRequestableModuleIds(systemInfo);
        const hasNewModules = requestableModuleIds.some((id) => form.requested_modules?.[id]);
        const acceptancePayload = buildAgreementAcceptancePayload(form, user, {
          activeModuleIds,
          includeActiveForLegal: hasActiveLicense && !hasNewModules,
          catalog,
        });
        const acceptance = await createAgreementAcceptance(acceptancePayload);
        const aid = String(acceptance?.id || "").trim();
        if (!aid) {
          throw new Error("No se recibió identificador de aceptación legal.");
        }
        setAgreementAcceptanceId(aid);
        setLegalAccepted(true);
        setLegalLocked(true);
        setLegalReused(false);
        setReusedAcceptance(null);
      } catch (err) {
        setLegalAccepted(false);
        const detail = err?.response?.data?.detail;
        setLegalAcceptError(
          typeof detail === "string"
            ? detail
            : detail?.message || err?.message || LICENSE_REQUEST_MSG_LEGAL_LOAD_FAILED,
        );
      } finally {
        setLegalSaving(false);
      }
    },
    [catalog, form, legalCatalog, legalLocked, legalReused, legalSaving, systemInfo, user],
  );

  const canProceedWithRequest = Boolean(
    legalLocked && agreementAcceptanceId && !applicableLoading,
  );

  return {
    legalCatalog,
    legalLoading: legalLoading || applicableLoading,
    legalError,
    legalAccepted,
    legalLocked,
    legalSaving,
    legalAcceptError,
    agreementAcceptanceId,
    legalReused,
    reusedAcceptance,
    canProceedWithRequest,
    setLegalAccepted,
    handleLegalAcceptedChange,
  };
}
